import { AppLink } from "./AppLink";

type Props = {
  /** Market page uses “Switch automatically”; Store uses “Send automatically”. */
  variant: "market" | "store";
  marketsAuto: boolean;
  free: boolean;
  busy?: boolean;
  onAsk: () => void;
  onAuto: () => void;
};

/**
 * Ask first / auto - shared `markets_auto` setting.
 * Spec 6.3 / 6.4.3: choice list (not a single text line).
 */
export function VisitorModeSection({
  variant,
  marketsAuto,
  free,
  busy,
  onAsk,
  onAuto,
}: Props) {
  const autoLabel =
    variant === "market" ? "Switch automatically" : "Send automatically";
  const autoHint =
    variant === "market"
      ? free
        ? "Visitors see their currency right away. 300 automatic redirects a month - each automatic currency switch counts as one. After that, visitors are asked."
        : "Visitors see their currency right away. They can switch back anytime."
      : "Visitors are moved without asking. Shoppers with items in their cart are always asked first.";

  return (
    <s-section
      heading={
        variant === "market"
          ? "How visitors are switched"
          : "How visitors are sent"
      }
    >
      <s-stack gap="base">
        <label
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            cursor: busy ? "default" : "pointer",
          }}
        >
          <input
            type="radio"
            name={`sg-mode-${variant}`}
            checked={!marketsAuto}
            disabled={busy}
            onChange={() => onAsk()}
            style={{ marginTop: 4 }}
          />
          <s-stack gap="small-100">
            <s-text type="strong">Ask first · Recommended</s-text>
            <s-paragraph color="subdued">
              {variant === "market"
                ? "Popup: accept switches to their currency (e.g. Shop in CAD); decline keeps yours (e.g. Keep shopping in USD)."
                : "Popup: accept opens the other store; decline stays on this one. Cart with items always asks first."}
            </s-paragraph>
          </s-stack>
        </label>
        <label
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            cursor: busy ? "default" : "pointer",
          }}
        >
          <input
            type="radio"
            name={`sg-mode-${variant}`}
            checked={marketsAuto}
            disabled={busy}
            onChange={() => onAuto()}
            style={{ marginTop: 4 }}
          />
          <s-stack gap="small-100">
            <s-text type="strong">{autoLabel}</s-text>
            <s-paragraph color="subdued">{autoHint}</s-paragraph>
          </s-stack>
        </label>
        <s-paragraph color="subdued">
          Shoppers with items in their cart are always asked first.
          {variant === "store"
            ? " EU visitors going to another website are always asked first, for EU privacy rules."
            : " EU visitors and ad clicks are still asked first."}
        </s-paragraph>
        <s-paragraph color="subdued">
          {marketsAuto && free && variant === "market" ? (
            <>
              <AppLink to="/app/billing">Remove the limit</AppLink>
              {" · "}
            </>
          ) : null}
          Popup look: <AppLink to="/app/popup">Edit popup</AppLink>
          {" · "}
          Remember choice:{" "}
          <AppLink to="/app/settings">Settings</AppLink>
        </s-paragraph>
      </s-stack>
    </s-section>
  );
}
